// @flow
import React, { useState, useEffect } from 'react';
import injectSheet from 'react-jss';
import { useDispatch } from 'react-redux';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { flashMessage as __flashMessage } from '~/app/actions';
import TextInput from '~/app/components/TextInput';
import Button from '~/app/ui/Button';
import { S, DARK } from '~/app/ui/tokens';
import { PAGE_METADATA_KEYS } from 'shared/constants';

type Props = {
  classes: Object,
  pageId: string,
};

type State = {
  title?: string,
  description?: string,
  'og:title'?: string,
  'og:description'?: string,
  'og:image'?: string,
  [PAGE_METADATA_KEYS.SCHEMA]: string | undefined,
};

const PageMetaFields = [
  {
    label: 'Page Title',
    key: 'title',
    placeholder: 'Page Title'
  },
  {
    label: 'Page Description',
    key: 'description',
    placeholder: 'Page Description'
  },
  {
    label: 'OpenGraph Title',
    key: 'og:title',
    placeholder: 'Page Title'
  },
  {
    label: 'OpenGraph Description',
    key: 'og:description',
    placeholder: 'Page Description'
  },
  {
    label: 'OpenGraph Image',
    key: 'og:image',
    placeholder: 'Page Meta Image'
  },
  {
    label: 'Schema (JSON-LD)',
    key: PAGE_METADATA_KEYS.SCHEMA,
    placeholder: ''
  }
];

const EditPageMeta = (props: Props) => {
  const { pageId, classes: c } = props;
  const [draft, setDraft] = useState<State>({});

  const { push } = useHistory();

  const dispatch = useDispatch();
  const flashMessage = (message, type) => dispatch(__flashMessage(message, type));

  // getPage
  const { data } = useQuery(QUERY, {
    variables: { id: pageId }
  });
  const page = data?.page;
  const isPageLoaded = !!page;

  // updatePageMeta
  const [__updatePageMeta] = useMutation(ACTIONS, {
    refetchQueries: ['getPage']
  });
  const updatePageMeta = (id, metadata) => __updatePageMeta({
    variables: { id, metadata }
  });

  useEffect(() => {
    // only fill draft for the first time when draft has nothing
    if (Object.keys(draft).length === 0 && page) {
      const newDraft = {};
      (page.metadata || []).forEach((pair) => {
        newDraft[pair.key] = pair.value;
      });
      // Ensure all fields exist for Studio pages or pages with sparse metadata
      PageMetaFields.forEach((f) => {
        if (!(f.key in newDraft)) newDraft[f.key] = '';
      });
      setDraft(newDraft);
    }
  }, [draft, setDraft, page]);

  const handleMetaChange = (key, value) => {
    setDraft({
      ...draft,
      [key]: value
    });
  }


  const handleSave = async () => {
    if (!page) return;
    const metadata = Object.keys(draft).map(key => ({
      key,
      value: draft[key] ?? ''
    }));

    await updatePageMeta(pageId, metadata);
    flashMessage('Successfully saved page metadata!');
    push(page.editorType === 'studio' ? '/admin' : `/admin/page/${pageId}`);
  };

  const cancelTo = page?.editorType === 'studio' ? '/admin' : `/admin/page/${pageId}`;

  if (!pageId) return null;
  if (!isPageLoaded) return <div className={c.container}><span>Loading...</span></div>;

  return (
    <div className={c.container}>
      <div className={c.header}>
        <div className={c.title}>
          Edit Page Metadata
        </div>
      </div>
      <div className={c.body}>
        {
          PageMetaFields.map(field => (
            <div className={c.formRow} key={field.key}>
              <div className={c.fieldLabel}>
                {field.label}
              </div>
              <TextInput
                type="text"
                ariaLabel={field.label}
                placeholder={field.placeholder}
                value={draft[field.key]}
                additionalStyles={{ flexGrow: 1 }}
                onChange={e => handleMetaChange(field.key, e.target.value)}
              />
            </div>
          ))
        }
        <div className={c.actionGroup}>
          <Button variant="primary" onClick={handleSave} disabled={!isPageLoaded}>Save</Button>
          <Button to={cancelTo || '/admin'}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '20px 30px'
  },
  body: {},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  formRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    width: '80%'
  },
  fieldLabel: {
    width: '10%',
    fontSize: '12px',
    color: 'hsl(0, 0%, 30%)',
    [DARK]: { color: S.muted }
  },
  actionGroup: {
    display: 'flex',
    gap: 8,
    marginTop: '20px'
  }
};

/* =============================================================================
=    GraphQL: Get Metadata of the page
============================================================================= */
const QUERY = gql`
  query getPage($id: ID!) {
    page (id: $id) {
      _id
      editorType
      metadata
    }
  }
`;

/* =============================================================================
=    GraphQL: Update page
============================================================================= */
const ACTIONS = gql`
mutation updatePageMeta($id: String!, $metadata: [PageMetaInput]) {
  updatePageMeta(_id: $id, metadata: $metadata) {
    metadata
  }
}
`;

export default injectSheet(styles)(EditPageMeta);
